import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiPlus, FiEdit, FiTrash2, FiCalendar, FiVideo, FiSearch, FiLink, FiUser, FiCheck, FiX, FiRefreshCw, FiPlay, FiFileText } from 'react-icons/fi';
import { mockInterviewsAPI, studentsAPI, zoomAPI, settingsAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import Modal from '../../components/Common/Modal';
import { toast } from 'react-toastify';
import { getTodayDateInputMin, storedDatetimeToLocalInput } from '../../utils/futureDateTime';
import { getBrowserIanaTimezone, resolveDefaultInterviewTimezone } from '../../utils/browserTimezone';
import {
  formatInterviewSlotAvailabilitySuffix,
  canBypassInterviewSlotCapacity
} from '../../utils/interviewSlotAvailabilityLabel';
import { useTranslation } from 'react-i18next';
import { useCountry } from '../../context/CountryContext';
import { UnifiedFilterHeaderCell } from '../../components/Common/UnifiedTableFilters';
import { runTableFilters } from '../../utils/tableFilters';

const INTERVIEW_STATUSES = ['scheduled', 'completed', 'cancelled', 'no_show', 'student_did_not_join', 'rescheduled'];

/**
 * Human-readable label for a mock-interview status code. Uses i18n keys
 * (mockInterviews.status.<code> → interviews.status.<code>) with a final
 * fallback that replaces every underscore and title-cases the words —
 * so `student_did_not_join` becomes "Student Did Not Join".
 */
const formatInterviewStatus = (status, t) => {
  if (!status) return '';
  const translated = t
    ? t(`mockInterviews.status.${status}`, {
        defaultValue: t(`interviews.status.${status}`, { defaultValue: '' }),
      })
    : '';
  if (translated) return translated;
  return String(status)
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
};

const formatOutcomeLabel = (outcome, t) => {
  if (!outcome) return '';
  const translated = t(`mockInterviews.outcomes.${outcome}`, {
    defaultValue: t(`interviews.outcomes.${outcome}`, { defaultValue: '' })
  });
  if (translated) return translated;
  return String(outcome)
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
};

const actionGroupStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  padding: '3px',
  borderRadius: 10,
  border: '1px solid var(--border)',
  background: 'var(--bg-secondary)'
};

const actionBtnBaseStyle = {
  width: 28,
  height: 28,
  padding: 0,
  borderRadius: 8,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center'
};

const actionBtnStyle = (variant = 'outline') => {
  if (variant === 'success') {
    return {
      ...actionBtnBaseStyle,
      border: '1px solid var(--success)',
      background: 'var(--success-light)',
      color: 'var(--success)'
    };
  }
  if (variant === 'danger') {
    return {
      ...actionBtnBaseStyle,
      border: '1px solid var(--danger)',
      background: 'var(--danger-light)',
      color: 'var(--danger)'
    };
  }
  if (variant === 'warning') {
    return {
      ...actionBtnBaseStyle,
      border: '1px solid var(--warning)',
      background: 'var(--warning-light)',
      color: 'var(--warning)',
      fontSize: 9,
      fontWeight: 700
    };
  }
  return actionBtnBaseStyle;
};

const MockInterviewList = () => {
  const { t } = useTranslation();
  const { activeCountry } = useCountry();
  const showInterviewHostColumn = String(activeCountry || '').toUpperCase() === 'FRANCE';
  const { canPerformAction, user } = useAuth();
  const navigate = useNavigate();
  const [mockInterviews, setMockInterviews] = useState([]);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showRescheduleModal, setShowRescheduleModal] = useState(false);
  const [editingInterview, setEditingInterview] = useState(null);
  const [slotDate, setSlotDate] = useState(getTodayDateInputMin());
  const [slotId, setSlotId] = useState('');
  const [desiredSlotTime, setDesiredSlotTime] = useState('');
  const [modalCampusId, setModalCampusId] = useState(null);
  const [slotOptions, setSlotOptions] = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [timezones, setTimezones] = useState([]);
  const [savingReschedule, setSavingReschedule] = useState(false);
  const [rescheduleForm, setRescheduleForm] = useState({
    scheduled_at: '',
    timezone: getBrowserIanaTimezone(),
    interview_link: '',
    remarks: ''
  });
  const [filters, setFilters] = useState({ search: '', studentId: '', status: '', startDate: '', endDate: '' });
  const [tableFilters, setTableFilters] = useState({ globalSearch: '', columns: {} });
  const isSalesExecutive = String(user?.role || '').toLowerCase() === 'sales_executive';
  const selectedStudentCampusId = useMemo(() => {
    if (modalCampusId) return modalCampusId;
    const sid = editingInterview?.student_id;
    if (!sid) return null;
    const st = (students || []).find((s) => String(s.student_id) === String(sid));
    return st?.campus_id || null;
  }, [modalCampusId, editingInterview?.student_id, students]);

  const loadMockInterviews = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.studentId) params.studentId = filters.studentId;
      if (filters.status) params.status = filters.status;
      if (filters.startDate) params.startDate = filters.startDate;
      if (filters.endDate) params.endDate = filters.endDate;

      const res = await mockInterviewsAPI.getAll(params);
      let data = res.data || [];
      if (filters.search) {
        const s = filters.search.toLowerCase();
        data = data.filter(i =>
          `${i.first_name} ${i.last_name}`.toLowerCase().includes(s) ||
          i.student_ref?.toLowerCase().includes(s) ||
          i.remarks?.toLowerCase().includes(s)
        );
      }

      if (isSalesExecutive) {
        const allowedStudentIds = new Set((students || []).map((st) => String(st.student_id)));
        data = data.filter((i) => allowedStudentIds.has(String(i?.student_id)));
      }
      setMockInterviews(data);
    } catch (err) {
      toast.error(t('mockInterviews.failedLoad'));
    }
    setLoading(false);
  }, [filters, isSalesExecutive, students, t]);

  useEffect(() => { loadMockInterviews(); }, [loadMockInterviews]);
  useEffect(() => {
    studentsAPI.getAll().then(r => setStudents(r.data.students || r.data || [])).catch(() => {});
    settingsAPI.getInterviewTimezones().then((res) => {
      const activeZones = (res.data || []).filter((tz) => tz.status === 'active');
      setTimezones(activeZones);
      if (activeZones.length > 0) {
        setRescheduleForm((prev) => ({ ...prev, timezone: resolveDefaultInterviewTimezone(activeZones) }));
      }
    }).catch(() => {});
  }, []);

  const handleDelete = async (id) => {
    if (!window.confirm(t('mockInterviews.deleteConfirm'))) return;
    try {
      await mockInterviewsAPI.delete(id);
      toast.success(t('mockInterviews.deleted'));
      loadMockInterviews();
    } catch {
      toast.error(t('mockInterviews.deleteFailed'));
    }
  };

  const handleMarkOutcome = async (id, outcome) => {
    try {
      await mockInterviewsAPI.update(id, { outcome });
      toast.success(t('mockInterviews.markedAs', { outcome: formatOutcomeLabel(outcome, t) }));
      loadMockInterviews();
    } catch (err) {
      toast.error(err.response?.data?.message || t('mockInterviews.outcomeUpdateFailed'));
    }
  };

  const handleReschedule = async (interview) => {
    let campusId = null;
    try {
      const r = await studentsAPI.getById(interview.student_id);
      campusId = r?.data?.campus_id || null;
    } catch (_) {}
    setModalCampusId(campusId);
    const localInput = storedDatetimeToLocalInput(interview.scheduled_at);
    const dateStr = localInput ? localInput.slice(0, 10) : getTodayDateInputMin();
    const timeStr = localInput ? localInput.slice(11, 16) : '';
    setEditingInterview(interview);
    setSlotDate(dateStr);
    setSlotId('');
    setDesiredSlotTime(timeStr);
    setRescheduleForm({
      scheduled_at: localInput || `${dateStr}T${timeStr}`,
      timezone: interview.timezone || resolveDefaultInterviewTimezone(timezones),
      interview_link: interview.interview_link || '',
      remarks: interview.remarks || ''
    });
    setShowRescheduleModal(true);
  };

  useEffect(() => {
    if (!showRescheduleModal || !slotDate) return;
    setSlotsLoading(true);
    settingsAPI.getInterviewSlotAvailability({
      date: slotDate,
      type: 'mock',
      campus_id: selectedStudentCampusId || undefined
    })
      .then((res) => setSlotOptions(res.data || []))
      .catch(() => setSlotOptions([]))
      .finally(() => setSlotsLoading(false));
  }, [showRescheduleModal, slotDate, selectedStudentCampusId]);

  useEffect(() => {
    if (!showRescheduleModal) return;
    const selected = (slotOptions || []).find((s) => String(s.slot_id) === String(slotId));
    if (!selected) return;
    const start = String(selected.start_time || '').slice(0, 5);
    setRescheduleForm((f) => ({ ...f, scheduled_at: `${slotDate}T${start}` }));
  }, [showRescheduleModal, slotId, slotDate, slotOptions]);

  useEffect(() => {
    if (!showRescheduleModal) return;
    if (slotId) return;
    if (!desiredSlotTime) return;
    const match = (slotOptions || []).find(
      (s) => String(s.start_time || '').slice(0, 5) === String(desiredSlotTime).slice(0, 5)
    );
    if (match?.slot_id != null) setSlotId(String(match.slot_id));
  }, [showRescheduleModal, slotOptions, desiredSlotTime, slotId]);

  const submitReschedule = async () => {
    if (!editingInterview?.mock_interview_id || !rescheduleForm.scheduled_at || !rescheduleForm.timezone) {
      toast.error(t('mockInterviews.fillRequired'));
      return;
    }
    try {
      setSavingReschedule(true);
      await mockInterviewsAPI.update(editingInterview.mock_interview_id, {
        scheduled_at: rescheduleForm.scheduled_at,
        interview_link: rescheduleForm.interview_link || null,
        timezone: rescheduleForm.timezone || getBrowserIanaTimezone(),
        remarks: rescheduleForm.remarks || null,
        status: 'rescheduled',
        outcome: 'pending'
      });
      toast.success(t('mockInterviews.rescheduled'));
      setShowRescheduleModal(false);
      setEditingInterview(null);
      setSlotId('');
      await loadMockInterviews();
    } catch (err) {
      toast.error(err.response?.data?.message || t('mockInterviews.rescheduleFailed'));
    } finally {
      setSavingReschedule(false);
    }
  };

  const formatDateTime = (dt) => {
    if (!dt) return '-';
    const s = String(dt).trim();
    const datePart = s.slice(0, 10);
    const timePart = s.length >= 16 ? s.slice(11, 16) : '';
    const [y, m, d] = datePart.split('-');
    if (!y || !m || !d) return '-';
    const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    const monthName = months[parseInt(m, 10) - 1] || m;
    return `${parseInt(d, 10)} ${monthName} ${y}${timePart ? ', ' + timePart : ''}`;
  };

  const canCreateMockInterviews = canPerformAction('mock_interviews', 'create');
  const canUpdateMockInterviews = canPerformAction('mock_interviews', 'update');
  const canDeleteMockInterviews = canPerformAction('mock_interviews', 'delete');
  // Reschedule is intentionally available to every authenticated user — the
  // backend enforces this via a dedicated reschedule-only bypass on
  // PUT /api/mock-interviews/:id.  Edit / delete continue to honour the
  // normal `mock_interviews.update` / `mock_interviews.delete` permissions.
  const canRescheduleMockInterviews = !!user;
  const canScheduleMockInterviews = canCreateMockInterviews || canUpdateMockInterviews;
  const columnFilterDefs = useMemo(() => {
    const timezones = Array.from(new Set((mockInterviews || []).map((i) => String(i.timezone || '')).filter(Boolean)));
    const createdBy = Array.from(new Set((mockInterviews || []).map((i) => String(i.created_by_name || '')).filter(Boolean)));
    return [
      { key: 'student', label: t('interviews.studentLabel'), field: 'student_full_name', type: 'text' },
      { key: 'student_language', label: t('interviews.tableStudentLanguage'), field: 'student_language', type: 'multi', options: [{ value: 'ENGLISH', label: t('studentForm.languageEnglish') }, { value: 'FRENCH', label: t('studentForm.languageFrench') }] },
      { key: 'interview_host', label: t('interviews.tableInterviewHost'), field: 'interview_host_name', type: 'text' },
      { key: 'scheduled_at', label: t('interviews.tableScheduledAt'), field: 'scheduled_at', type: 'date' },
      { key: 'timezone', label: t('interviews.tableTimezone'), field: 'timezone', type: 'multi', options: timezones.map((v) => ({ value: v, label: v })) },
      { key: 'status', label: t('interviews.tableStatus'), field: 'status', type: 'multi', options: INTERVIEW_STATUSES.map((v) => ({ value: v, label: formatInterviewStatus(v, t) })) },
      { key: 'outcome', label: t('interviews.tableOutcome'), field: 'outcome', type: 'multi', options: [{ value: 'pending', label: formatOutcomeLabel('pending', t) }, { value: 'pass', label: formatOutcomeLabel('pass', t) }, { value: 'fail', label: formatOutcomeLabel('fail', t) }, { value: 'student_did_not_join', label: formatOutcomeLabel('student_did_not_join', t) }] },
      { key: 'created_by', label: t('interviews.tableCreatedBy'), field: 'created_by_name', type: 'multi', options: createdBy.map((v) => ({ value: v, label: v })) }
    ];
  }, [mockInterviews, t]);
  const columnFilterMap = useMemo(() => Object.fromEntries(columnFilterDefs.map((d) => [d.key, d])), [columnFilterDefs]);
  const updateColumnFilters = useCallback((updater) => setTableFilters((prev) => ({ ...prev, columns: updater(prev.columns || {}) })), []);
  const normalizedInterviews = useMemo(
    () => (mockInterviews || []).map((i) => ({ ...i, student_full_name: `${i.title || ''} ${i.first_name || ''} ${i.last_name || ''} ${i.student_ref || ''}`.trim() })),
    [mockInterviews]
  );
  const filteredMockInterviews = useMemo(
    () => runTableFilters(normalizedInterviews, tableFilters, tableFilters, [], columnFilterDefs),
    [normalizedInterviews, tableFilters, columnFilterDefs]
  );

  return (
    <div className="page-container">
      <div className="page-header">
        <div><h1>{t('mockInterviews.title')}</h1><p>{t('mockInterviews.subtitle')}</p></div>
        {canScheduleMockInterviews && <button className="btn btn-primary" onClick={() => navigate('/mock-interviews/new')}><FiPlus /> {t('mockInterviews.scheduleBtn')}</button>}
      </div>

      <div className="table-container" style={{ marginBottom: 16 }}>
        <div className="table-toolbar">
          <div className="toolbar-left">
            <div className="table-search">
              <FiSearch className="search-icon" />
              <input type="text" placeholder={t('mockInterviews.searchPlaceholder')} value={tableFilters.globalSearch} onChange={(e) => setTableFilters((prev) => ({ ...prev, globalSearch: e.target.value }))} />
            </div>
            <div className="filter-bar">
              <select value={filters.studentId} onChange={(e) => setFilters({ ...filters, studentId: e.target.value })}>
                <option value="">{t('mockInterviews.allStudents')}</option>
                {students.map(s => <option key={s.student_id} value={s.student_id}>{s.student_ref} - {s.title} {s.first_name} {s.last_name}</option>)}
              </select>
              <select value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })}>
                <option value="">{t('mockInterviews.allStatuses')}</option>
                {INTERVIEW_STATUSES.map(s => <option key={s} value={s}>{formatInterviewStatus(s, t)}</option>)}
              </select>
              <input type="date" value={filters.startDate} onChange={(e) => setFilters({ ...filters, startDate: e.target.value })} />
              <input type="date" value={filters.endDate} onChange={(e) => setFilters({ ...filters, endDate: e.target.value })} />
            </div>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="loading-spinner"><div className="spinner"></div></div>
      ) : filteredMockInterviews.length === 0 ? (
        <div className="empty-state"><FiVideo size={48} /><p>{t('mockInterviews.noMockInterviews')}</p></div>
      ) : (
        <div className="card">
          <div className="table-responsive">
            <table>
              <thead>
                <tr>
                  <UnifiedFilterHeaderCell column={columnFilterMap.student} draftColumns={tableFilters.columns} onChange={updateColumnFilters} />
                  <UnifiedFilterHeaderCell column={columnFilterMap.student_language} draftColumns={tableFilters.columns} onChange={updateColumnFilters} />
                  {showInterviewHostColumn && <UnifiedFilterHeaderCell column={columnFilterMap.interview_host} draftColumns={tableFilters.columns} onChange={updateColumnFilters} />}
                  <UnifiedFilterHeaderCell column={columnFilterMap.scheduled_at} draftColumns={tableFilters.columns} onChange={updateColumnFilters} />
                  <UnifiedFilterHeaderCell column={columnFilterMap.timezone} draftColumns={tableFilters.columns} onChange={updateColumnFilters} />
                  <th>{t('interviews.tableLink')}</th>
                  <UnifiedFilterHeaderCell column={columnFilterMap.status} draftColumns={tableFilters.columns} onChange={updateColumnFilters} />
                  <UnifiedFilterHeaderCell column={columnFilterMap.outcome} draftColumns={tableFilters.columns} onChange={updateColumnFilters} />
                  <UnifiedFilterHeaderCell column={columnFilterMap.created_by} draftColumns={tableFilters.columns} onChange={updateColumnFilters} />
                  {canRescheduleMockInterviews && <th>{t('common.actions')}</th>}
                </tr>
              </thead>
              <tbody>
                {filteredMockInterviews.map(interview => (
                  <tr key={interview.mock_interview_id}>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <FiUser />
                        <div
                          role="button"
                          tabIndex={0}
                          onClick={() => navigate(`/students/${interview.student_id}`)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              navigate(`/students/${interview.student_id}`);
                            }
                          }}
                          style={{ cursor: 'pointer' }}
                          title={t('studentDetail.title')}
                        >
                          <div style={{ fontWeight: 500, color: 'var(--primary)' }}>
                            {interview.title} {interview.first_name} {interview.last_name}
                          </div>
                          <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                            {interview.student_ref}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td style={{ fontSize: 13 }}>
                      {interview.student_language === 'FRENCH'
                        ? t('studentForm.languageFrench')
                        : interview.student_language === 'ENGLISH'
                          ? t('studentForm.languageEnglish')
                          : '—'}
                    </td>
                    {showInterviewHostColumn && (
                      <td style={{ fontSize: 13 }}>{interview.interview_host_name || '—'}</td>
                    )}
                    <td><div style={{ display: 'flex', alignItems: 'center', gap: 6 }}><FiCalendar style={{ color: 'var(--text-muted)' }} /><span>{formatDateTime(interview.scheduled_at)}</span></div></td>
                    <td>{interview.timezone || getBrowserIanaTimezone()}</td>
                    <td>
                      {interview.interview_link ? (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                          <a href={interview.interview_link} target="_blank" rel="noopener noreferrer"
                            style={{ display: 'flex', alignItems: 'center', gap: 4, color: 'var(--primary)' }}>
                            <FiLink /> {t('interviews.joinLink')}
                          </a>
                          {interview.zoom_meeting_id && (
                            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                              {interview.zoom_recording_url && (
                                <a href={interview.zoom_recording_url} target="_blank" rel="noopener noreferrer"
                                  style={{ fontSize: 11, color: '#059669', display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                                  <FiPlay size={10} /> {t('interviews.recording')}
                                </a>
                              )}
                              {interview.zoom_transcript_url && (
                                <a href={interview.zoom_transcript_url} target="_blank" rel="noopener noreferrer"
                                  style={{ fontSize: 11, color: '#7c3aed', display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                                  <FiFileText size={10} /> {t('interviews.transcript')}
                                </a>
                              )}
                              {interview.zoom_duration_minutes && (
                                <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{interview.zoom_duration_minutes} min</span>
                              )}
                              <button title={t('interviews.syncZoom')} className="btn btn-sm btn-outline" style={{ padding: '2px 6px', fontSize: 11 }}
                                onClick={async () => {
                                  try {
                                    await zoomAPI.sync({ entity_type: 'mock_interview', entity_id: interview.mock_interview_id });
                                    toast.success(t('interviews.zoomSynced'));
                                    loadMockInterviews();
                                  } catch (err) { toast.error(t('interviews.syncFailed')); }
                                }}>
                                <FiRefreshCw size={11} />
                              </button>
                            </div>
                          )}
                        </div>
                      ) : <span style={{ color: 'var(--text-muted)' }}>-</span>}
                    </td>
                    <td>{formatInterviewStatus(interview.status, t)}</td>
                    <td>{interview.outcome ? formatOutcomeLabel(interview.outcome, t) : '-'}</td>
                    <td style={{ fontSize: 13 }}>{interview.created_by_name || '-'}</td>
                    {canRescheduleMockInterviews && (
                      <td>
                        <div style={{ display: 'flex', gap: 6 }}>
                          {canUpdateMockInterviews && interview.outcome === 'pending' && (
                            <div style={actionGroupStyle}>
                              <button className="btn btn-sm btn-success" style={actionBtnStyle('success')} onClick={() => handleMarkOutcome(interview.mock_interview_id, 'pass')} title={t('interviews.markPass')}><FiCheck /></button>
                              <button className="btn btn-sm btn-danger" style={actionBtnStyle('danger')} onClick={() => handleMarkOutcome(interview.mock_interview_id, 'fail')} title={t('interviews.markFail')}><FiX /></button>
                              <button className="btn btn-sm btn-warning" style={actionBtnStyle('warning')} onClick={() => handleMarkOutcome(interview.mock_interview_id, 'student_did_not_join')} title={t('interviews.markDidNotJoin')}>DNJ</button>
                              <button className="btn btn-sm btn-outline" style={actionBtnStyle()} onClick={() => handleReschedule(interview)} title={t('studentDetail.reschedule')}><FiRefreshCw /></button>
                            </div>
                          )}
                          {!canUpdateMockInterviews && interview.outcome === 'pending' && (
                            <button className="btn btn-sm btn-outline" style={actionBtnStyle()} onClick={() => handleReschedule(interview)} title={t('studentDetail.reschedule')}><FiRefreshCw /></button>
                          )}
                          {canUpdateMockInterviews && (
                            <button className="btn btn-sm btn-outline" style={actionBtnStyle()} onClick={() => navigate(`/mock-interviews/${interview.mock_interview_id}`)} title={t('common.edit')}><FiEdit /></button>
                          )}
                          {canUpdateMockInterviews && canDeleteMockInterviews && (
                            <button className="btn btn-sm btn-outline btn-danger" style={actionBtnStyle('danger')} onClick={() => handleDelete(interview.mock_interview_id)} title={t('common.delete')}><FiTrash2 /></button>
                          )}
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      <Modal
        isOpen={showRescheduleModal}
        onClose={() => {
          setShowRescheduleModal(false);
          setEditingInterview(null);
          setModalCampusId(null);
          setSlotId('');
          setDesiredSlotTime('');
        }}
        title={t('studentDetail.reschedule')}
      >
        <div className="form-grid">
          <div className="form-group">
            <label>{t('interviews.dateLabel')} <span style={{ color: 'var(--danger)' }}>*</span></label>
            <input
              type="date"
              value={slotDate}
              min={getTodayDateInputMin()}
              onChange={(e) => {
                setSlotDate(e.target.value);
                setSlotId('');
              }}
              required
            />
          </div>
          <div className="form-group">
            <label>{t('interviews.slotLabel')} <span style={{ color: 'var(--danger)' }}>*</span></label>
            <select value={slotId} onChange={(e) => setSlotId(e.target.value)} required disabled={slotsLoading || !slotDate}>
              <option value="">
                {slotsLoading ? t('studentDetail.modals.loadingSlots') : (slotOptions.length ? t('studentDetail.modals.selectSlot') : t('studentDetail.modals.noSlotsConfigured'))}
              </option>
              {slotOptions.map((s) => {
                const start = String(s.start_time || '').slice(0, 5);
                const end = String(s.end_time || '').slice(0, 5);
                const label = s.label || `${start} - ${end}`;
                const isFull = !!s.is_full;
                const bypass = canBypassInterviewSlotCapacity(user);
                const disabled = !bypass && isFull;
                const capText = formatInterviewSlotAvailabilitySuffix(s);
                return (
                  <option key={s.slot_id} value={String(s.slot_id)} disabled={disabled}>
                    {label} — {capText}{disabled ? ` ${t('studentDetail.modals.full')}` : ''}
                  </option>
                );
              })}
            </select>
          </div>
          <div className="form-group">
            <label>{t('interviews.timezoneLabel')}</label>
            <select
              value={rescheduleForm.timezone}
              onChange={(e) => setRescheduleForm((f) => ({ ...f, timezone: e.target.value }))}
            >
              {timezones.length > 0 ? (
                timezones.map((tz) => (
                  <option key={tz.timezone_id} value={tz.timezone_name}>
                    {tz.timezone_label || tz.timezone_name}
                  </option>
                ))
              ) : (
                <option value={getBrowserIanaTimezone()}>{getBrowserIanaTimezone()}</option>
              )}
            </select>
          </div>
          <div className="form-group">
            <label>{t('interviews.interviewLink')}</label>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <input
                type="url"
                value={rescheduleForm.interview_link}
                onChange={(e) => setRescheduleForm((f) => ({ ...f, interview_link: e.target.value }))}
                placeholder={t('interviews.linkPlaceholder')}
                style={{ flex: 1 }}
              />
              {rescheduleForm.scheduled_at && (
                <button
                  type="button"
                  className="btn btn-outline btn-sm"
                  onClick={async () => {
                    try {
                      const needsInterviewHost =
                        showInterviewHostColumn &&
                        String(editingInterview?.student_language || '').toUpperCase() === 'FRENCH';
                      const hostUserId = editingInterview?.interview_host_user_id;
                      if (needsInterviewHost && !hostUserId) {
                        toast.error(t('interviews.frenchHostRequired'));
                        return;
                      }
                      const { data } = await zoomAPI.generate({
                        topic: `Mock Interview - ${editingInterview?.first_name || ''}`.trim() || 'Mock Interview',
                        start_time: rescheduleForm.scheduled_at,
                        duration: 60,
                        timezone: rescheduleForm.timezone || resolveDefaultInterviewTimezone(timezones),
                        entity_type: 'mock_interview',
                        entity_id: editingInterview?.mock_interview_id || undefined,
                        host_user_id: needsInterviewHost ? hostUserId : undefined
                      });
                      setRescheduleForm((f) => ({ ...f, interview_link: data?.join_url || f.interview_link }));
                      toast.success(t('interviews.zoomGenerated'));
                    } catch (err) {
                      toast.error(err.response?.data?.message || t('interviews.zoomFailed'));
                    }
                  }}
                >
                  <FiVideo size={14} /> {t('interviews.generateZoom')}
                </button>
              )}
            </div>
          </div>
          <div className="form-group" style={{ gridColumn: '1 / -1' }}>
            <label>{t('interviews.remarksLabel')}</label>
            <textarea
              rows={3}
              value={rescheduleForm.remarks}
              onChange={(e) => setRescheduleForm((f) => ({ ...f, remarks: e.target.value }))}
            />
          </div>
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
          <button className="btn btn-outline" onClick={() => setShowRescheduleModal(false)}>
            {t('common.cancel')}
          </button>
          <button className="btn btn-primary" onClick={submitReschedule} disabled={savingReschedule}>
            {savingReschedule ? t('common.saving') : t('interviews.rescheduleBtn')}
          </button>
        </div>
      </Modal>
    </div>
  );
};

export default MockInterviewList;
